import { Component, DestroyRef, inject, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute, Router } from '@angular/router';
import { ToastrService } from 'ngx-toastr';
import { BatchForCreation, BatchRepositoryService, Bundle, BundlesRepositoryService, ConfigurationState, ConfirmDialogComponent, eBatchType, SelectOption } from 'reg-hub-common';
import { TranslocoService } from '@jsverse/transloco';

@Component({
    selector: 'app-bundle',
    templateUrl: './bundle.component.html',
    styleUrl: './bundle.component.css',
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class BundleComponent implements OnInit {
  private readonly transloco = inject(TranslocoService);
  private destroyRef = inject(DestroyRef);

  bundleID: string = '';
  bundle!: Bundle;
  selectedIndex: number = 0;
  isSaving: boolean = false;
  _batchTypes: { label: string, value: eBatchType }[] = [
    { label: 'bundles.batchTypes.assetSearch', value: eBatchType.assetSearch },
    { label: 'bundles.batchTypes.debtorSearch', value: eBatchType.debtorSearch },
    { label: 'bundles.batchTypes.regNumberSearch', value: eBatchType.registrationNumberSearch },
    { label: 'bundles.batchTypes.lien', value: eBatchType.lien },
    { label: 'bundles.batchTypes.renewal', value: eBatchType.renewal },
    { label: 'bundles.batchTypes.discharge', value: eBatchType.discharge },
    { label: 'bundles.batchTypes.caBusinessSearch', value: eBatchType.caBusinessSearch },
    { label: 'bundles.batchTypes.caBusinessReport', value: eBatchType.caBusinessReport },
    { label: 'bundles.batchTypes.usBusinessSearch', value: eBatchType.usBusinessSearch },
    { label: 'bundles.batchTypes.usBusinessReport', value: eBatchType.usBusinessReport },
    { label: 'bundles.batchTypes.uboReport', value: eBatchType.ultimateBeneficialOwnershipReport },
    { label: "VINsight", value: eBatchType.vinSight },
  ]

  constructor(private activatedRoute: ActivatedRoute,
    private bundlesRepo: BundlesRepositoryService,
    private configurationStateService: ConfigurationState,
    private batchesService: BatchRepositoryService,
    private router: Router,
    private messageService: ToastrService,
    private dialog: MatDialog) { }

  ngOnInit(): void {
    this.activatedRoute.paramMap.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(params => {
      this.bundleID = params.get('bundleID')!;
      this.getBundle();
    });

    var defaultTab = this.activatedRoute.snapshot.queryParams['tab'];
    if (defaultTab !== null && defaultTab !== undefined) {
      switch(defaultTab)
            {
                case "orders":
                    this.selectedIndex = 1;
                    break;
                case "batches":
                    this.selectedIndex = 0;
                    break;
                default:
                    this.selectedIndex = 0;

            }
    }
  }

  getBundle() {
    this.configurationStateService.getCorporationID().then(corporationID => {
      this.bundlesRepo.populateBaseLink([corporationID]);
      this.bundlesRepo.getBundle(this.bundleID).subscribe(bundle => {
        this.bundle = bundle;
      })
    })
  }

  deleteBundleConfirmation() {
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      data: {
        title: this.transloco.translate('bundles.deleteColection'),
        confirmationPrompt: this.transloco.translate('bundles.confirmPermanentlyDeleteThisBundleAll'),
        positiveResponseLabel: this.transloco.translate('common.buttons.delete'),
        negativeResponseLabel: this.transloco.translate('common.buttons.cancel')
      },
      minWidth: '800px'
    });

    dialogRef.afterClosed().pipe(takeUntilDestroyed(this.destroyRef)).subscribe(result => {
      if (result) {
        // submission is confirmed, submit order
        this.deleteBundle();
      }
    });
  }

  deleteBundle() {
    this.bundlesRepo.deleteBundle(this.bundleID)
      .subscribe(_ => {
        this.router.navigate(['bundles']);
      });
  }

  addBatch(eBatchType: eBatchType) {
    this.isSaving = true;
    let newBatch: BatchForCreation = {
      batchTypeID: eBatchType,
      description: `${this.bundle.name} - ${eBatchType}`,
      submissionRateInSeconds: 5
    };

    this.batchesService.createBatch(this.bundleID, newBatch).subscribe({
      next: (batch) => {
        this.router.navigate(['bundles', this.bundleID, 'draft-batch', batch.id]);
        this.isSaving = false;
      },
      error: err => {
        console.error(err);
        this.messageService.error(this.transloco.translate('bundles.failedToCreateBatch'));
        this.isSaving = false;
      },
    })
  }

  addBundleOrders() {
    this.router.navigate(['orders'], { queryParams: { targetBundleID: this.bundleID } })
  }
}
